/* eslint no-shadow: off */
import api from '@/api';
import {
  Client, ClientEarlyBirdDto, Spouse, EarlyBirdQualification, EarlyBirdEnrollment, Paystub, Referral,
} from '@/model';
import { RAF_STATE } from '@/enum/RAFStatuses';
import { STATE, STATE_DESCRIPTION_MAP } from '@/enum/EarlyBirdApplicationStates';

const getDefaultState = () => ({
  client: new Client(),
  paystubs: [],
  referrals: [],
  ebQualification: undefined,
  ebEnrollment: undefined,
  ebOptout: undefined,
});

const state = getDefaultState();

const getters = {
  client: state => state.client,
  spouse: state => state.client.spouse,
  paystubs: state => state.paystubs,
  referrals: state => state.referrals,
  ebQualification: state => state.ebQualification,
  ebEnrollment: state => state.ebEnrollment,
  ebApplicationState: (state, getters) => {
    const qualifications = getters.ebQualification;
    const enrollment = getters.ebEnrollment;
    if (getters.ebOptout) {
      return STATE.OPT_OUT;
    }
    if (qualifications && qualifications.id) {
      if (qualifications.submitted) {
        if (qualifications.isApproved) {
          if (enrollment && enrollment.id) {
            return STATE.ENROLLED;
          }
          return STATE.QUALIFIED;
        }
        return STATE.DOES_NOT_QUALIFY;
      }
      return STATE.IN_PROGRESS;
    }
    return STATE.NOT_STARTED;
  },
  ebOptout: state => state.ebOptout,
  ebApplicationStateDescription: (state, getters) => STATE_DESCRIPTION_MAP[getters.ebApplicationState],
  ebApplicationComplete: (state, getters) => [STATE.DOES_NOT_QUALIFY, STATE.ENROLLED].indexOf(getters.ebApplicationState) !== -1 || getters.ebOptout === true,
  ebApplicationEnrolled: (state, getters) => getters.ebApplicationState === STATE.ENROLLED,
  hasReferralsReadyForPayout: (state, getters) => {
    const referrals = getters.referrals;
    return referrals.findIndex(referral => referral.status === RAF_STATE.APPROVED && !referral.transactionId) > -1;
  },
};

const actions = {
  async loadClient({ dispatch }, id) {
    try {
      const response = await api.client.get(id);
      dispatch('setClient', response.data);
    } catch (e) {
      dispatch('setClient', null);
      // dispatch('clientResponse', null);
      throw e;
    }
  },
  setClient({ commit }, clientData) {
    commit('setClient', clientData);
  },
  async loadClientEarlyBird({ dispatch }, clientId) {
    try {
      const response = await api.client.getEarlyBird(clientId);
      dispatch('setClientEarlyBird', response.data);
    } catch (e) {
      dispatch('setClientEarlyBird', null);
      throw e;
    }
  },
  setClientEarlyBird({ commit }, earlyBirdDto) {
    commit('setClientEarlyBird', earlyBirdDto);
  },
  async loadClientPaystubs({ dispatch }, clientId) {
    try {
      const response = await api.client.getPaystubs(clientId);
      dispatch('setClientPaystubs', response.data);
    } catch (e) {
      dispatch('setClientPaystubs', []);
      throw e;
    }
  },
  setClientPaystubs({ commit }, paystubs) {
    commit('setClientPaystubs', paystubs);
  },
  async savePaystub({ getters, commit }, paystub) {
    paystub.clientId = getters.client.id;
    const response = await api.paystub.save(paystub);
    commit('addOrUpdatePaystub', response.data);
  },
  async loadClientReferrals({ dispatch }, clientId) {
    try {
      const response = await api.client.getReferrals(clientId);
      dispatch('setClientReferrals', response.data);
    } catch (e) {
      dispatch('setClientReferrals', []);
      throw e;
    }
  },
  setClientReferrals({ commit }, referrals) {
    commit('setClientReferrals', referrals);
  },
  updateClient({ commit }, propertiesToUpdate) {
    commit('updateClient', propertiesToUpdate);
  },
  updateSpouse({ getters, commit }, propertiesToUpdate) {
    const spouse = getters.spouse;
    commit('updateSpouse', { ...spouse, ...propertiesToUpdate });
  },
  setQualification({ commit }, qualification) {
    commit('setQualification', qualification);
  },
  setEnrollment({ commit }, enrollment) {
    commit('setEnrollment', enrollment);
  },
  async save({ commit, getters }) {
    const client = getters.client;
    const response = await api.client.saveClient(client);
    commit('setClient', response.data);

    return getters.client;
  },
  async optoutOfEarlyBirdProgram({ commit }) {
    await api.ebOptout.optout(state.client.id);
    commit('setOptout', true);
  },
  async submitQualification({ commit, getters }) {
    const client = getters.client;
    const qualification = new EarlyBirdQualification(getters.ebQualification);
    qualification.submitted = true;
    const response = await api.client.submitEarlyBirdQualification(client.id, qualification);
    commit('setQualification', response.data);
  },
  async submitEnrollment({ commit, getters }) {
    const client = getters.client;
    const enrollment = new EarlyBirdQualification(getters.ebEnrollment);
    const response = await api.client.submitEarlyBirdEnrollment(client.id, enrollment);
    commit('setEnrollment', response.data);
  },
  async submitEnrollmentWithApproval({ commit, getters }, accessToken) {
    const client = getters.client;
    const enrollment = new EarlyBirdQualification(getters.ebEnrollment);
    const response = await api.client.submitEarlyBirdEnrollmentWithApproval(client.id, enrollment, accessToken);
    commit('setEnrollment', response.data);
  },
  addReferral({ commit }, referral) {
    commit('addReferral', referral);
  },
  async removeEBEnrollment({ commit, getters }) {
    const enrollment = getters.ebEnrollment;
    if (enrollment) {
      await api.ebEnrollment.removeEnrollment(enrollment);
      commit('removeEnrollment');
    }
  },
  async overrideReferral({ commit }, referral) {
    const response = await api.referral.override(referral);
    commit('updateReferral', response.data);
  },
  async changeReferralOffice({ commit }, updateObject) {
    const response = await api.referral.changeOffice(updateObject.referral.id, updateObject.officeId);
    commit('updateReferral', response.data);
  },
  async removeReferral({ commit }, referral) {
    await api.referral.remove(referral.id);
    commit('removeReferral', referral);
  },
  resetState({ commit }) {
    commit('resetState');
  },
};

const mutations = {
  setClient(state, client) {
    state.client = new Client(client);
  },
  setClientEarlyBird(state, earlyBirdDto) {
    earlyBirdDto = new ClientEarlyBirdDto(earlyBirdDto);
    state.ebQualification = earlyBirdDto.qualification;
    state.ebEnrollment = earlyBirdDto.enrollment;
    state.ebOptout = earlyBirdDto.didOptOut;
  },
  updateClient(state, propertiesToUpdate) {
    state.client = new Client({
      ...state.client,
      ...propertiesToUpdate,
    });
  },
  updateSpouse(state, spouse) {
    state.client.spouse = new Spouse(spouse);
  },
  setClientPaystubs(state, paystubs = []) {
    state.paystubs = paystubs.map(data => new Paystub(data));
  },
  setClientReferrals(state, referrals = []) {
    state.referrals = referrals.map(data => new Referral(data));
  },
  addReferral(state, referral) {
    state.referrals.push(referral);
  },
  addOrUpdatePaystub(state, paystub) {
    const index = state.paystubs.findIndex(p => p.id === paystub.id);
    if (index > -1) {
      state.paystubs.splice(index, 1, paystub);
    } else {
      state.paystubs.push(paystub);
    }
  },
  setQualification(state, qualification) {
    state.ebQualification = new EarlyBirdQualification(qualification);
  },
  setEnrollment(state, enrollment) {
    state.ebEnrollment = new EarlyBirdEnrollment(enrollment);
  },
  removeEnrollment(state) {
    state.ebEnrollment = undefined;
  },
  setOptout(state, val) {
    state.ebOptout = val;
  },
  updateReferral(state, referralData) {
    const referral = new Referral(referralData);
    const index = state.referrals.findIndex(r => r.id === referral.id);

    state.referrals.splice(index, 1, referral);
  },
  removeReferral(state, referralData) {
    const referral = new Referral(referralData);
    const index = state.referrals.findIndex(r => r.id === referral.id);

    state.referrals.splice(index, 1);
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
